import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {extractSvgMarkup} from '../safe-svg.pipe';

/**
 * Category / subcategory visual with a stable fallback chain:
 * SVG markup → optional photo URL → letter mark from the name.
 * Empty categories (no listings, no SVG) always get a letter placeholder.
 */
@Component({
  standalone: false,
  selector: 'app-category-visual',
  templateUrl: './category-visual.component.html',
  styleUrls: ['./category-visual.component.scss'],
})
export class CategoryVisualComponent implements OnChanges {
  /** Category / subcategory display name (drives letter fallback). */
  @Input() name: string | null | undefined;
  /** Inline SVG markup from taxonomy. */
  @Input() svg: string | null | undefined;
  /** Optional photo (uploaded art or popular listing thumbnail). */
  @Input() imageUrl: string | null | undefined;
  /** card = browse tiles; sm = sidebar rows. */
  @Input() size: 'card' | 'sm' = 'card';

  svgHtml: SafeHtml | null = null;
  resolvedImage: string | null = null;
  letter = '?';
  private imageFailed = false;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['svg'] || changes['name'] || changes['imageUrl']) {
      this.imageFailed = false;
      this.recompute();
    }
  }

  onImageError(): void {
    this.imageFailed = true;
    this.resolvedImage = null;
  }

  private recompute(): void {
    const markup = extractSvgMarkup(this.svg);
    if (markup) {
      this.svgHtml = this.sanitizer.bypassSecurityTrustHtml(markup);
      this.resolvedImage = null;
    } else {
      this.svgHtml = null;
      const url = `${this.imageUrl || ''}`.trim();
      this.resolvedImage = !this.imageFailed && url ? url : null;
    }
    this.letter = this.initialFromName(this.name);
  }

  private initialFromName(name: string | null | undefined): string {
    const trimmed = `${name || ''}`.trim();
    if (!trimmed) {
      return '?';
    }
    // Skip "All …" prefix so "All Lighting" → L
    const withoutAll = trimmed.replace(/^all[\s-]+/i, '').trim() || trimmed;
    const ch = withoutAll.charAt(0);
    return /[a-z0-9]/i.test(ch) ? ch.toUpperCase() : '?';
  }
}
